/*
 * Preact context for the leaf metadata from `leafMeta.ts`.
 *
 * A context of its own rather than a field on the data source: the source is
 * interchangeable (simulator, SignalR, later MQTT on the panel), but the
 * metadata always comes from the plant schema. Without a provider the hook
 * returns `undefined` and every widget behaves exactly as before, so the
 * preview page and the palette need nothing.
 */

import { createContext } from 'preact';
import type { ComponentChildren } from 'preact';
import { useContext, useMemo } from 'preact/hooks';
import { parseBindingKey } from './bindingKey';
import type { HmiLeafMeta } from './leafMeta';

/** Metadata for a binding key `{plantId}/{leafId}`. */
export type LeafMetaLookup = (bindingKey: string | null | undefined) => HmiLeafMeta | undefined;

const LeafMetaContext = createContext<LeafMetaLookup | null>(null);

/** `byLeafId` looks up by lower-case leaf GUID, like the index from
 *  `buildLeafMetaIndex`. Binding keys are resolved here. */
export function LeafMetaProvider(
  { byLeafId, children }: { byLeafId: (leafId: string) => HmiLeafMeta | undefined; children?: ComponentChildren },
) {
  const lookup = useMemo<LeafMetaLookup>(() => (key) => {
    const parsed = parseBindingKey(key);
    return parsed ? byLeafId(parsed.leafId) : undefined;
  }, [byLeafId]);
  return <LeafMetaContext.Provider value={lookup}>{children}</LeafMetaContext.Provider>;
}

/** Hands an existing lookup on. For widget trees that are rendered with
 *  their own `render()` call (the editor canvas mounts each grid cell
 *  separately) and therefore do not inherit context. */
export function LeafMetaLookupProvider(
  { lookup, children }: { lookup: LeafMetaLookup | null; children?: ComponentChildren },
) {
  return <LeafMetaContext.Provider value={lookup}>{children}</LeafMetaContext.Provider>;
}

/** Metadata of the leaf behind a binding key, or `undefined`. */
export function useLeafMeta(bindingKey: string | null | undefined): HmiLeafMeta | undefined {
  return useContext(LeafMetaContext)?.(bindingKey);
}

/** The whole lookup, for the editor (defaults on first binding). */
export function useLeafMetaLookup(): LeafMetaLookup | null {
  return useContext(LeafMetaContext);
}
